import React, { useState, useEffect } from 'react';
import { PlusIcon, ChevronDownIcon, ChevronRightIcon, PencilIcon, TrashIcon, CalendarIcon, FlagIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, doc, getDocs, setDoc, deleteDoc, query, where } from 'firebase/firestore';
import { DragDropContext, Droppable, Draggable, DropResult, DroppableProvided, DraggableProvided, DraggableStateSnapshot, DroppableStateSnapshot } from 'react-beautiful-dnd';

export interface Goal {
  id: string;
  uid: string;
  title: string;
  description: string;
  deadline: string;
  status: 'not_started' | 'in_progress' | 'completed';
  subgoals: SubGoal[];
  createdAt: string;
  updatedAt?: string;
  completedAt?: string;
  tags?: string[];
  priority?: 'low' | 'medium' | 'high';
  notes?: string;
  attachments?: { name: string; url: string }[];
  category?: string;
}

export interface SubGoal {
  id: string;
  uid: string;
  title: string;
  description: string;
  deadline: string;
  status: 'not_started' | 'in_progress' | 'completed';
  createdAt: string;
  priority?: 'low' | 'medium' | 'high';
}

export interface DailyGoal {
  id: string;
  uid: string;
  title: string;
  completed: boolean;
  date: string;
}

const statusColors = {
  not_started: 'bg-gray-100 text-gray-800',
  in_progress: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800'
};

const statusLabels = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  completed: 'Completed'
};

export default function Goals() {
  const { currentUser } = useAuth();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [dailyGoals, setDailyGoals] = useState<DailyGoal[]>([]);
  const [newDailyGoalTitle, setNewDailyGoalTitle] = useState('');
  const [showNewGoal, setShowNewGoal] = useState(false);
  const [showNewSubGoal, setShowNewSubGoal] = useState<string | null>(null);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [editingSubGoal, setEditingSubGoal] = useState<{ goalId: string; subGoal: SubGoal } | null>(null);
  const [expandedGoals, setExpandedGoals] = useState<Set<string>>(new Set());
  const [selectedTab, setSelectedTab] = useState<'longterm' | 'daily' | null>(null);
  const [newGoal, setNewGoal] = useState<Partial<Goal>>({
    title: '',
    description: '',
    deadline: '',
    status: 'not_started',
    subgoals: []
  });
  const [newSubGoal, setNewSubGoal] = useState<Partial<SubGoal>>({
    title: '',
    description: '',
    deadline: '',
    status: 'not_started'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'not_started' | 'in_progress' | 'completed'>('all');
  const [sortBy, setSortBy] = useState<'due' | 'priority' | 'custom' | 'created' | 'updated' | 'completed'>('due');
  const [dailyStreak, setDailyStreak] = useState(0);
  const motivationalQuotes = [
    "Small steps every day lead to big results.",
    "Stay focused and never give up!",
    "Progress, not perfection.",
    "You are capable of amazing things.",
    "Dream big, start small, act now."
  ];
  const [quote, setQuote] = useState('');
  const quickDailyTemplates = [
    "Review notes",
    "Read 10 pages",
    "Practice flashcards",
    "Watch a tutorial",
    "Summarize what I learned"
  ];
  const [editingNotesGoalId, setEditingNotesGoalId] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [attachmentDraft, setAttachmentDraft] = useState<{ name: string; url: string }>({ name: '', url: '' });
  const [showCongrats, setShowCongrats] = useState(false);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [filterPriority, setFilterPriority] = useState<'all' | 'low' | 'medium' | 'high'>('all');
  const [goalsOrder, setGoalsOrder] = useState<string[]>([]);

  useEffect(() => {
    if (!currentUser) return;
    const today = new Date().toISOString().split('T')[0];

    const fetchGoals = async () => {
      const goalsRef = collection(db, 'goals');
      const q = query(goalsRef, where('uid', '==', currentUser.uid));
      const querySnapshot = await getDocs(q);
      const userGoals = querySnapshot.docs.map(doc => doc.data() as Goal);
      setGoals(userGoals);
    };

    const fetchDailyGoals = async () => {
      const dailyGoalsRef = collection(db, 'dailyGoals');
      const q = query(dailyGoalsRef, where('uid', '==', currentUser.uid), where('date', '==', today));
      const querySnapshot = await getDocs(q);
      const userDailyGoals = querySnapshot.docs.map(doc => doc.data() as DailyGoal);
      setDailyGoals(userDailyGoals);
    };

    fetchGoals();
    fetchDailyGoals();
  }, [currentUser]);

  useEffect(() => {
    setQuote(motivationalQuotes[Math.floor(Math.random() * motivationalQuotes.length)]);
    setDailyStreak(dailyGoals.every(g => g.completed) && dailyGoals.length > 0 ? 1 : 0);
  }, [dailyGoals]);

  useEffect(() => {
    // Initialize order if not set
    if (goals.length && goalsOrder.length === 0) {
      setGoalsOrder(goals.map(g => g.id));
    }
  }, [goals]);

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.type === 'GOAL') {
      const newOrder = Array.from(goalsOrder);
      const [removed] = newOrder.splice(result.source.index, 1);
      newOrder.splice(result.destination.index, 0, removed);
      setGoalsOrder(newOrder);
    } else if (result.type.startsWith('SUBGOAL-')) {
      const goalId = result.type.replace('SUBGOAL-', '');
      const goal = goals.find(g => g.id === goalId);
      if (!goal) return;
      const newSubgoals = Array.from(goal.subgoals);
      const [removed] = newSubgoals.splice(result.source.index, 1);
      newSubgoals.splice(result.destination.index, 0, removed);
      goal.subgoals = newSubgoals;
      setGoals([...goals]);
    }
  };

  const handleAddDailyGoal = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!newDailyGoalTitle.trim() || !currentUser) return;

    const today = new Date().toISOString().split('T')[0];
    const dailyGoalId = doc(collection(db, 'dailyGoals')).id;
    const dailyGoal: DailyGoal = {
      id: dailyGoalId,
      uid: currentUser.uid,
      title: newDailyGoalTitle,
      completed: false,
      date: today,
    };

    await setDoc(doc(db, 'dailyGoals', dailyGoalId), dailyGoal);
    setDailyGoals([...dailyGoals, dailyGoal]);
    setNewDailyGoalTitle('');
  };

  const handleToggleDailyGoal = async (goal: DailyGoal) => {
    const updatedGoal = { ...goal, completed: !goal.completed };
    await setDoc(doc(db, 'dailyGoals', goal.id), updatedGoal);
    setDailyGoals(dailyGoals.map(dg => dg.id === goal.id ? updatedGoal : dg));
  };

  const handleDeleteDailyGoal = async (goalId: string) => {
    await deleteDoc(doc(db, 'dailyGoals', goalId));
    setDailyGoals(dailyGoals.filter(dg => dg.id !== goalId));
  };

  const handleSaveGoal = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!newGoal.title || !newGoal.deadline || !currentUser) return;

    const goalId = editingGoal?.id || doc(collection(db, 'goals')).id;
    const goal: Goal = {
      id: goalId,
      uid: currentUser.uid,
      title: newGoal.title,
      description: newGoal.description || '',
      deadline: newGoal.deadline,
      status: newGoal.status || 'not_started',
      subgoals: editingGoal?.subgoals || [],
      createdAt: editingGoal?.createdAt || new Date().toISOString()
    };

    await setDoc(doc(db, 'goals', goalId), goal);

    if (editingGoal) {
      const updatedGoals = goals.map(g => g.id === goal.id ? goal : g);
      setGoals(updatedGoals);
    } else {
      setGoals([...goals, goal]);
    }

    setShowNewGoal(false);
    setEditingGoal(null);
    setNewGoal({ title: '', description: '', deadline: '', status: 'not_started', subgoals: [] });
  };

  const handleSaveSubGoal = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!newSubGoal.title || !newSubGoal.deadline || !showNewSubGoal || !currentUser) return;

    const parentGoal = goals.find(g => g.id === showNewSubGoal);
    if (!parentGoal) return;

    const subGoal: SubGoal = {
      id: editingSubGoal?.subGoal.id || doc(collection(db, 'goals')).id,
      uid: currentUser.uid,
      title: newSubGoal.title,
      description: newSubGoal.description || '',
      deadline: newSubGoal.deadline,
      status: newSubGoal.status || 'not_started',
      createdAt: editingSubGoal?.subGoal.createdAt || new Date().toISOString()
    };

    let updatedSubgoals: SubGoal[];
    if (editingSubGoal) {
      updatedSubgoals = parentGoal.subgoals.map(sg => sg.id === subGoal.id ? subGoal : sg);
    } else {
      updatedSubgoals = [...parentGoal.subgoals, subGoal];
    }
    
    const updatedGoal = { ...parentGoal, subgoals: updatedSubgoals };

    await setDoc(doc(db, 'goals', parentGoal.id), updatedGoal);

    const updatedGoals = goals.map(g => g.id === parentGoal.id ? updatedGoal : g);
    setGoals(updatedGoals);

    setShowNewSubGoal(null);
    setEditingSubGoal(null);
    setNewSubGoal({ title: '', description: '', deadline: '', status: 'not_started' });
  };

  const handleDeleteGoal = async (goalId: string) => {
    if (window.confirm('Are you sure you want to delete this goal and all its subgoals?')) {
      await deleteDoc(doc(db, 'goals', goalId));
      const updatedGoals = goals.filter(g => g.id !== goalId);
      setGoals(updatedGoals);
    }
  };

  const handleDeleteSubGoal = async (goalId: string, subGoalId: string) => {
    if (window.confirm('Are you sure you want to delete this subgoal?')) {
      const parentGoal = goals.find(g => g.id === goalId);
      if (!parentGoal) return;

      const updatedSubgoals = parentGoal.subgoals.filter(sg => sg.id !== subGoalId);
      const updatedGoal = { ...parentGoal, subgoals: updatedSubgoals };

      await setDoc(doc(db, 'goals', parentGoal.id), updatedGoal);

      const updatedGoals = goals.map(g => g.id === parentGoal.id ? updatedGoal : g);
      setGoals(updatedGoals);
    }
  };

  const toggleGoalExpansion = (goalId: string) => {
    const newExpanded = new Set(expandedGoals);
    if (newExpanded.has(goalId)) {
      newExpanded.delete(goalId);
    } else {
      newExpanded.add(goalId);
    }
    setExpandedGoals(newExpanded);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="w-full p-4 sm:px-8 lg:px-16 xl:px-32 min-h-[80vh]">
      {/* Selection UI */}
      {selectedTab === null && (
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <h1 className="text-2xl sm:text-3xl font-bold mb-10 text-center">What do you want to focus on?</h1>
          <div className="flex flex-col sm:flex-row gap-8 w-full justify-center">
            {/* Daily Goals Card */}
            <button
              onClick={() => setSelectedTab('daily')}
              className="flex-1 bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 flex flex-col items-center transition-transform hover:-translate-y-1 hover:shadow-2xl border-2 border-transparent hover:border-teal-500 focus:outline-none"
            >
              <CalendarIcon className="w-12 h-12 text-teal-500 mb-4" />
              <div className="text-xl font-bold mb-2">Daily Goals</div>
              <div className="text-gray-500 text-center">Set and check off your focus for today. Stay on track, one day at a time!</div>
            </button>
            {/* Long-term Goals Card */}
            <button
              onClick={() => setSelectedTab('longterm')}
              className="flex-1 bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 flex flex-col items-center transition-transform hover:-translate-y-1 hover:shadow-2xl border-2 border-transparent hover:border-blue-500 focus:outline-none"
            >
              <FlagIcon className="w-12 h-12 text-blue-500 mb-4" />
              <div className="text-xl font-bold mb-2">Long-term Goals</div>
              <div className="text-gray-500 text-center">Plan, track, and achieve your bigger learning ambitions over time.</div>
            </button>
          </div>
        </div>
      )}

      {/* Daily Goals Section */}
      {selectedTab === 'daily' && (
        <div className="w-full bg-white dark:bg-gray-800 shadow-lg px-8 py-8 animate-fade-in" style={{ borderRadius: '18px' }}>
          <button onClick={() => setSelectedTab(null)} className="flex items-center text-gray-500 hover:text-blue-500 mb-6">
            <ArrowLeftIcon className="w-5 h-5 mr-2" /> Back
          </button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <span className="text-lg font-bold">🔥 Streak: {dailyStreak} day{dailyStreak !== 1 ? 's' : ''}</span>
              <span className="text-sm italic text-gray-500">{quote}</span>
            </div>
            <div className="flex gap-2">
              {quickDailyTemplates.map(tpl => (
                <button key={tpl} onClick={() => setNewDailyGoalTitle(tpl)} className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200">{tpl}</button>
              ))}
            </div>
          </div>
          {/* Completion animation */}
          {dailyGoals.length > 0 && dailyGoals.every(g => g.completed) && (
            <div className="flex items-center justify-center my-4 animate-bounce">
              <span className="text-4xl text-green-500 mr-2">✔️</span>
              <span className="text-green-600 font-bold text-xl">All daily goals completed! Great job!</span>
            </div>
          )}
          <form onSubmit={handleAddDailyGoal} className="flex gap-2 mb-4">
            <input
              type="text"
              value={newDailyGoalTitle}
              onChange={(e) => setNewDailyGoalTitle(e.target.value)}
              placeholder="Add a new goal for today..."
              className="flex-grow w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-blue-300"
              disabled={!newDailyGoalTitle.trim()}
            >
              Add
            </button>
          </form>
          <div className="space-y-2">
            {dailyGoals.length > 0 ? (
              dailyGoals.map(goal => (
                <div key={goal.id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={goal.completed}
                      onChange={() => handleToggleDailyGoal(goal)}
                      className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className={`ml-3 text-gray-800 dark:text-gray-200 ${goal.completed ? 'line-through text-gray-500' : ''}`}>
                      {goal.title}
                    </span>
                  </div>
                  <button
                    onClick={() => handleDeleteDailyGoal(goal.id)}
                    className="p-1 text-gray-400 hover:text-red-500"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              ))
            ) : (
              <p className="text-gray-500">No goals for today. Add one to get started!</p>
            )}
          </div>
        </div>
      )}

      {/* Long-term Goals Section */}
      {selectedTab === 'longterm' && (
        <div className="w-full bg-white dark:bg-gray-800 shadow-lg px-8 py-8 animate-fade-in" style={{ borderRadius: '18px' }}>
          <button onClick={() => setSelectedTab(null)} className="flex items-center text-gray-500 hover:text-blue-500 mb-6" aria-label="Back to goal selection">
            <ArrowLeftIcon className="w-5 h-5 mr-2" /> Back
          </button>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex gap-2 flex-wrap">
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search goals..."
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value as any)} className="px-2 py-2 border rounded-md">
                <option value="all">All Statuses</option>
                <option value="not_started">Not Started</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
              <select value={filterPriority} onChange={e => setFilterPriority(e.target.value as any)} className="px-2 py-2 border rounded-md">
                <option value="all">All Priorities</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
              <select value={filterTag} onChange={e => setFilterTag(e.target.value)} className="px-2 py-2 border rounded-md">
                <option value="">All Tags</option>
                {allTags.map(tag => <option key={tag} value={tag}>{tag}</option>)}
              </select>
              <select value={sortBy} onChange={e => setSortBy(e.target.value as any)} className="px-2 py-2 border rounded-md">
                <option value="due">Due Date</option>
                <option value="priority">Priority</option>
                <option value="created">Created</option>
                <option value="updated">Updated</option>
                <option value="completed">Completed</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <button
              onClick={() => setShowNewGoal(true)}
              className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              New Goal
            </button>
          </div>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="goals-droppable" type="GOAL">
              {(provided: DroppableProvided, _snapshot: DroppableStateSnapshot) => (
                <div className="space-y-4 mt-4" ref={provided.innerRef} {...provided.droppableProps}>
                  {goalsOrder.map((goalId, idx) => {
                    const goal = goals.find(g => g.id === goalId);
                    if (!goal) return null;
                    const completedSubgoals = goal.subgoals.filter(sg => sg.status === 'completed').length;
                    const progress = goal.subgoals.length > 0 ? Math.round((completedSubgoals / goal.subgoals.length) * 100) : 0;
                    const dueSoon = new Date(goal.deadline) < new Date(Date.now() + 3 * 24 * 60 * 60 * 1000) && goal.status !== 'completed';
                    const overdue = new Date(goal.deadline) < new Date() && goal.status !== 'completed';
                    const isExpanded = expandedGoals.has(goal.id);
                    return (
                      <Draggable key={goal.id} draggableId={goal.id} index={idx}>
                        {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`bg-gray-50 dark:bg-gray-700 rounded-xl shadow-soft overflow-hidden p-4 border-l-4 ${overdue ? 'border-red-500' : dueSoon ? 'border-yellow-400' : goal.status === 'completed' ? 'border-green-500' : 'border-transparent'} ${snapshot.isDragging ? 'ring-2 ring-blue-400' : ''}`}
                            tabIndex={0}
                            aria-label={`Goal: ${goal.title}`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span {...provided.dragHandleProps} className="cursor-move" aria-label="Drag to reorder goal">
                                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="6" cy="6" r="1.5"/><circle cx="6" cy="12" r="1.5"/><circle cx="6" cy="18" r="1.5"/><circle cx="12" cy="6" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="18" r="1.5"/><circle cx="18" cy="6" r="1.5"/><circle cx="18" cy="12" r="1.5"/><circle cx="18" cy="18" r="1.5"/></svg>
                                </span>
                                <button onClick={() => setExpandedGoals(prev => { const s = new Set(prev); s.has(goal.id) ? s.delete(goal.id) : s.add(goal.id); return s; })} className="text-gray-500 hover:text-blue-500 focus:outline-none" aria-label={isExpanded ? 'Collapse goal' : 'Expand goal'}>
                                  {isExpanded ? <ChevronDownIcon className="w-5 h-5" /> : <ChevronRightIcon className="w-5 h-5" />}
                                </button>
                                <span className="font-bold text-lg">{goal.title}</span>
                                {goal.priority && (
                                  <span className={`px-2 py-1 rounded text-xs font-semibold ${goal.priority === 'high' ? 'bg-red-200 text-red-800' : goal.priority === 'medium' ? 'bg-yellow-200 text-yellow-800' : 'bg-green-200 text-green-800'}`}>{goal.priority}</span>
                                )}
                                {goal.tags && goal.tags.map(tag => (
                                  <span key={tag} className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-700 flex items-center gap-1">
                                    {tag}
                                    <button onClick={() => { goal.tags = goal.tags!.filter(t => t !== tag); setAllTags(tags => tags.filter(t => t !== tag)); }} className="ml-1 text-xs text-red-500" aria-label={`Remove tag ${tag}`}>×</button>
                                  </span>
                                ))}
                                {overdue && <span className="px-2 py-1 rounded text-xs bg-red-500 text-white">Overdue</span>}
                                {dueSoon && !overdue && <span className="px-2 py-1 rounded text-xs bg-yellow-400 text-white">Due Soon</span>}
                              </div>
                              <span className="text-sm text-gray-500">Due: {formatDate(goal.deadline)}</span>
                              <button
                                onClick={() => { if (window.confirm('Delete this goal and all its subgoals?')) handleDeleteGoal(goal.id); }}
                                className="p-1 text-gray-500 hover:text-red-500 ml-2"
                                aria-label="Delete goal"
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                            </div>
                            <div className="mb-2">
                              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                                <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${progress}%` }} />
                              </div>
                              <span className="text-xs text-gray-500">Progress: {progress}%</span>
                            </div>
                            <div className="mb-2 text-gray-700 dark:text-gray-200">{goal.description}</div>
                            {/* Timeline/history */}
                            <div className="mb-2 text-xs text-gray-400 flex gap-2 items-center">
                              <span>Created: {formatDate(goal.createdAt)}</span>
                              {goal.updatedAt && <span>• Updated: {formatDate(goal.updatedAt)}</span>}
                              {goal.completedAt && <span>• Completed: {formatDate(goal.completedAt)}</span>}
                            </div>
                            {/* Notes editing */}
                            <div className="mb-2">
                              <span className="font-semibold text-xs text-gray-500">Notes:</span>
                              {editingNotesGoalId === goal.id ? (
                                <div className="flex gap-2 mt-1">
                                  <textarea
                                    value={notesDraft}
                                    onChange={e => setNotesDraft(e.target.value)}
                                    className="w-full px-2 py-1 border rounded"
                                    rows={2}
                                  />
                                  <button onClick={() => { setEditingNotesGoalId(null); goal.notes = notesDraft; }} className="px-2 py-1 bg-blue-500 text-white rounded">Save</button>
                                </div>
                              ) : (
                                <div className="flex gap-2 items-center mt-1">
                                  <span className="italic text-sm text-gray-500">{goal.notes || 'No notes yet.'}</span>
                                  <button onClick={() => { setEditingNotesGoalId(goal.id); setNotesDraft(goal.notes || ''); }} className="text-xs text-blue-500 underline">Edit</button>
                                </div>
                              )}
                            </div>
                            {/* Tag/category management */}
                            <div className="mb-2 flex flex-wrap gap-2 items-center">
                              <span className="font-semibold text-xs text-gray-500">Tags:</span>
                              {goal.tags && goal.tags.map(tag => (
                                <span key={tag} className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-700 flex items-center gap-1">
                                  {tag}
                                  <button onClick={() => { goal.tags = goal.tags!.filter(t => t !== tag); setAllTags(tags => tags.filter(t => t !== tag)); }} className="ml-1 text-xs text-red-500" aria-label={`Remove tag ${tag}`}>×</button>
                                </span>
                              ))}
                              <input
                                type="text"
                                value={tagInput}
                                onChange={e => setTagInput(e.target.value)}
                                placeholder="Add tag"
                                className="px-2 py-1 border rounded text-xs"
                                onKeyDown={e => { if (e.key === 'Enter' && tagInput) { goal.tags = [...(goal.tags || []), tagInput]; setAllTags(tags => tags.includes(tagInput) ? tags : [...tags, tagInput]); setTagInput(''); e.preventDefault(); } }}
                              />
                              <button onClick={() => { if (tagInput) { goal.tags = [...(goal.tags || []), tagInput]; setAllTags(tags => tags.includes(tagInput) ? tags : [...tags, tagInput]); setTagInput(''); } }} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">Add</button>
                            </div>
                            {/* Priority editing */}
                            <div className="mb-2 flex gap-2 items-center">
                              <span className="font-semibold text-xs text-gray-500">Priority:</span>
                              <select value={goal.priority || ''} onChange={e => { goal.priority = e.target.value as any; }} className="px-2 py-1 border rounded text-xs">
                                <option value="">None</option>
                                <option value="high">High</option>
                                <option value="medium">Medium</option>
                                <option value="low">Low</option>
                              </select>
                            </div>
                            {/* Attachments UI (with file upload placeholder) */}
                            <div className="mb-2">
                              <span className="font-semibold text-xs text-gray-500">Attachments:</span>
                              <div className="flex gap-2 mt-1 flex-wrap">
                                {goal.attachments && goal.attachments.map(att => (
                                  <a key={att.url} href={att.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline text-xs">{att.name}</a>
                                ))}
                                <input
                                  type="text"
                                  placeholder="Link name"
                                  value={attachmentDraft.name}
                                  onChange={e => setAttachmentDraft(d => ({ ...d, name: e.target.value }))}
                                  className="px-2 py-1 border rounded text-xs"
                                />
                                <input
                                  type="url"
                                  placeholder="https://..."
                                  value={attachmentDraft.url}
                                  onChange={e => setAttachmentDraft(d => ({ ...d, url: e.target.value }))}
                                  className="px-2 py-1 border rounded text-xs"
                                />
                                <button onClick={() => { if (attachmentDraft.name && attachmentDraft.url) { goal.attachments = [...(goal.attachments || []), { ...attachmentDraft }]; setAttachmentDraft({ name: '', url: '' }); } }} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">Add</button>
                                <button className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs cursor-not-allowed" title="File upload coming soon!">Upload File</button>
                              </div>
                            </div>
                            {/* Collapsible Subgoals (with drag-and-drop) */}
                            {isExpanded && (
                              <Droppable droppableId={`subgoals-${goal.id}`} type={`SUBGOAL-${goal.id}`}>
                                {(provided: DroppableProvided, _snapshot: DroppableStateSnapshot) => (
                                  <div className="mt-2" ref={provided.innerRef} {...provided.droppableProps}>
                                    <div className="font-semibold mb-1">Subgoals</div>
                                    <ul className="ml-4 list-disc">
                                      {goal.subgoals.map((sg, sgIdx) => (
                                        <Draggable key={sg.id} draggableId={sg.id} index={sgIdx}>
                                          {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                                            <li
                                              ref={provided.innerRef}
                                              {...provided.draggableProps}
                                              className={`flex items-center gap-2 text-sm bg-white dark:bg-gray-800 rounded px-2 py-1 mb-1 ${snapshot.isDragging ? 'ring-2 ring-blue-400' : ''}`}
                                              tabIndex={0}
                                              aria-label={`Subgoal: ${sg.title}`}
                                            >
                                              <span {...provided.dragHandleProps} className="cursor-move" aria-label="Drag to reorder subgoal">
                                                <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="6" cy="12" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="18" cy="12" r="1.5"/></svg>
                                              </span>
                                              <span className={sg.status === 'completed' ? 'line-through text-gray-400' : ''}>{sg.title}</span>
                                              <select value={sg.priority || ''} onChange={e => { sg.priority = e.target.value as any; }} className="px-2 py-0.5 border rounded text-xs">
                                                <option value="">None</option>
                                                <option value="high">High</option>
                                                <option value="medium">Medium</option>
                                                <option value="low">Low</option>
                                              </select>
                                              <span className="text-xs text-gray-400">{sg.status}</span>
                                              <button
                                                onClick={() => { if (window.confirm('Delete this subgoal?')) handleDeleteSubGoal(goal.id, sg.id); }}
                                                className="p-1 text-gray-500 hover:text-red-500 ml-2"
                                                aria-label="Delete subgoal"
                                              >
                                                <TrashIcon className="w-4 h-4" />
                                              </button>
                                            </li>
                                          )}
                                        </Draggable>
                                      ))}
                                      {provided.placeholder}
                                    </ul>
                                  </div>
                                )}
                              </Droppable>
                            )}
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      )}

      {/* New/Edit Goal Modal */}
      {showNewGoal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <form onSubmit={handleSaveGoal} className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingGoal ? 'Edit Goal' : 'New Goal'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={newGoal.title}
                  onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={newGoal.description}
                  onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Deadline
                </label>
                <input
                  type="date"
                  value={newGoal.deadline}
                  onChange={(e) => setNewGoal({ ...newGoal, deadline: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <select
                  value={newGoal.status}
                  onChange={(e) => setNewGoal({ ...newGoal, status: e.target.value as Goal['status'] })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="not_started">Not Started</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowNewGoal(false);
                    setEditingGoal(null);
                    setNewGoal({ title: '', description: '', deadline: '', status: 'not_started', subgoals: [] });
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  {editingGoal ? 'Save Changes' : 'Add Goal'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* New/Edit SubGoal Modal */}
      {showNewSubGoal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <form onSubmit={handleSaveSubGoal} className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingSubGoal ? 'Edit Subgoal' : 'New Subgoal'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={newSubGoal.title}
                  onChange={(e) => setNewSubGoal({ ...newSubGoal, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={newSubGoal.description}
                  onChange={(e) => setNewSubGoal({ ...newSubGoal, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Deadline
                </label>
                <input
                  type="date"
                  value={newSubGoal.deadline}
                  onChange={(e) => setNewSubGoal({ ...newSubGoal, deadline: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <select
                  value={newSubGoal.status}
                  onChange={(e) => setNewSubGoal({ ...newSubGoal, status: e.target.value as SubGoal['status'] })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="not_started">Not Started</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowNewSubGoal(null);
                    setEditingSubGoal(null);
                    setNewSubGoal({ title: '', description: '', deadline: '', status: 'not_started' });
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  {editingSubGoal ? 'Save Changes' : 'Add Subgoal'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
} 